import {
  Cartesian3,
  Math as CesiumMath,
  Cartesian2,
  Cesium3DTileset,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  Cartographic,
  JulianDate,
  PolygonHierarchy,
  Entity,
  ConstantProperty,
  ImageMaterialProperty,
  defined,
  ConstantPositionProperty,
  CallbackProperty,
  Rectangle,
  Ellipsoid,
  EllipsoidGeodesic,
  PropertyBag,
} from "cesium";
import Type from "./Types";
import { toKML } from "@placemarkio/tokml";
import { toaster } from "../components/ui/toaster";
import { Box, createListCollection, defineStyle, Select, Text } from "@chakra-ui/react";
import { disableCameraControls, restoreCameraControls } from "./MapUtility";
import { joinTwoSelectedPolylinesOnce } from "./editing/JoinEntities";
import { enableReverseOnClick } from "./EditingGeojsonUtility";
import { duplicateEntity } from "./RightClickUtility";
import { markEntityLayerEdited } from "./entity/UpdateMapObjectEdited";
import { useAppStore } from "../context/store";

export const getGeojsonPropertiesKey = (geojson) => {
  const keys = new Set();

  const extractKeys = (obj) => {
    if (Array.isArray(obj)) {
      for (const item of obj) {
        extractKeys(item);
      }
    } else if (typeof obj === "object" && obj !== null) {
      for (const key in obj) {
        const value = obj[key];

        if (value === null || value === undefined) {
          keys.add(key);
          continue;
        }

        const type = typeof value;

        if (type === "string" || type === "number" || type === "boolean") {
          keys.add(key);
        } else if (type === "object") {
          extractKeys(value);
        }
      }
    }
  };

  for (const feature of geojson.features) {
    extractKeys(feature.properties);
  }

  return Array.from(keys);
};

export const getGeojsonListPropertiesKey = (geojsonList) => {
  const keys = new Set();

  const extractKeys = (obj) => {
    if (Array.isArray(obj)) {
      for (const item of obj) extractKeys(item);
      return;
    }

    if (typeof obj !== "object" || obj === null) return;

    for (const key in obj) {
      const value = obj[key];
      if (value === null) continue;

      const type = typeof value;
      if (type === "string" || type === "number" || type === "boolean") {
        keys.add(key);
      } else if (type === "object") {
        extractKeys(value);
      }
    }
  };

  if (!Array.isArray(geojsonList)) return [];

  for (const geojson of geojsonList) {
    if (!geojson || !Array.isArray(geojson.features)) continue;

    for (const feature of geojson.features) {
      extractKeys(feature?.properties);
    }
  }

  return Array.from(keys);
};

// read plain values from entity.properties at current time
export const getEntityPropertiesKeys = (entity) => {
  if (!entity || !entity.properties) return [];

  const props = entity.properties;
  const names = props.propertyNames || [];

  // return as plain array of strings
  return [...names];
};

export const extractEntityPropertiesDeep = (entity) => {
  const result = [];
  const now = JulianDate.now();

  if (!entity?.properties || !Array.isArray(entity.properties.propertyNames)) return result;

  // Keys to ignore (case-insensitive)
  const ignoreKeys = ["relations", "nodes"];

  const shouldIgnore = (key) => {
    return ignoreKeys.includes(key.toLowerCase());
  };

  const flattenObject = (obj, prefix = "") => {
    for (const key in obj) {
      if (!obj.hasOwnProperty(key)) continue;

      if (shouldIgnore(key)) continue;

      const val = obj[key];
      const value = typeof val?.getValue === "function" ? val.getValue(now) : val;
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === "object" && !Array.isArray(value)) {
        flattenObject(value, fullKey);
      } else {
        result.push({ key: fullKey, value });
      }
    }
  };

  entity.properties.propertyNames.forEach((propName) => {
    if (shouldIgnore(propName)) return;

    const propValue = entity.properties[propName];
    const value = typeof propValue?.getValue === "function" ? propValue.getValue(now) : propValue;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      flattenObject(value, propName);
    } else {
      result.push({ key: propName, value });
    }
  });

  return result;
};

// Function to fly the camera to a specific geographic location
export const flyToLocation = (viewer, longitude, latitude, height = null) => {
  if (!viewer || typeof longitude !== "number" || typeof latitude !== "number" || isNaN(longitude) || isNaN(latitude)) {
    console.warn("Invalid flyToLocation coordinates:", { longitude, latitude });
    return;
  }

  const fallbackHeight = viewer?.camera?.positionCartographic?.height;
  const finalHeight = typeof height === "number" && !isNaN(height) ? height : fallbackHeight;

  if (typeof finalHeight !== "number" || isNaN(finalHeight)) {
    console.warn("Invalid camera height for flyToLocation.");
    return;
  }

  viewer.camera.flyTo({
    destination: Cartesian3.fromDegrees(longitude, latitude, finalHeight),
    orientation: {
      heading: CesiumMath.toRadians(0),
      pitch: CesiumMath.toRadians(-45),
      roll: 0,
    },
    duration: 1,
  });
};

// Function to change camera view
export const moveCamaraView = (viewer, options) => {
  switch (options.type) {
    case Type.CAMERA.ENTITY:
      let firstEntity = options.entity;
      let position;

      // Try to extract a position from different possible geometry types
      if (firstEntity.position) {
        // For point entities
        position = firstEntity.position.getValue(viewer.clock.currentTime);
      } else if (firstEntity.polyline?.positions) {
        // For polyline entities: use the first point of the line
        const positions = firstEntity.polyline.positions.getValue(viewer.clock.currentTime);
        if (positions?.length > 0) {
          position = positions[0];
        }
      } else if (firstEntity.polygon?.hierarchy) {
        // For polygon entities: use the first point of the hierarchy
        const hierarchy = firstEntity.polygon.hierarchy.getValue(viewer.clock.currentTime);
        if (hierarchy?.positions?.length > 0) {
          position = hierarchy.positions[0];
        }
      }

      if (position && typeof position.x === "number" && typeof position.y === "number" && typeof position.z === "number" && !isNaN(position.x) && !isNaN(position.y) && !isNaN(position.z)) {
        try {
          const cartographic = Cartographic.fromCartesian(position);
          const lon = CesiumMath.toDegrees(cartographic.longitude);
          const lat = CesiumMath.toDegrees(cartographic.latitude);

          const camera = viewer.camera;
          const currentHeight = camera?.positionCartographic?.height;

          if (typeof lon === "number" && typeof lat === "number" && typeof currentHeight === "number" && !isNaN(lon) && !isNaN(lat) && !isNaN(currentHeight)) {
            const destination = Cartesian3.fromDegrees(lon, lat, currentHeight);
            camera.setView({
              destination,
            });
          } else {
            console.warn("Invalid lon/lat/height values:", { lon, lat, currentHeight });
          }
        } catch (e) {
          console.error("Failed to convert position to Cartographic:", e);
        }
      } else {
        console.warn("Invalid position object:", position);
      }

      break;

    case Type.CAMERA.DATASOURCE:
      // Not implemented yet
      break;
    case Type.CAMERA.LOCATION:
      // Not implemented yet
      break;
  }
};

// Function to show or hide the animation and timeline panels in the Cesium viewer
export const showOrHideTimeLine = (viewer, visibility = true) => {
  if (viewer) {
    // Show or hide the animation panel based on the 'visibility' flag
    viewer.animation.container.style.visibility = visibility ? "visible" : "hidden";

    // Show or hide the timeline panel based on the 'visibility' flag
    viewer.timeline.container.style.visibility = visibility ? "visible" : "hidden";
  }
};

// Function to create a unique ID for entities
export const createUniqueId = () => {
  // Generate a random unique ID with a "id-" prefix
  // Math.random().toString(36) generates a random base-36 string
  // substr(2, 9) trims the "0." from the start and takes the next 9 characters
  return "id-" + Math.random().toString(36).substr(2, 9);
};

// Function to select an entity by its ID in the Cesium viewer
export const selectEntityById = (viewer, entityId) => {
  if (!viewer || !viewer.entities) return null;
  if (entityId === undefined || entityId === null || entityId === "") {
    console.warn("selectEntityById called with invalid id:", entityId);
    return null;
  }

  const selected = safeGetById(viewer.entities, entityId);
  if (selected) {
    viewer.selectedEntity = selected;
    viewer.flyTo(selected);
    // retrievedGeojsonFrom1To2Sides(viewerRef, selected, selected.dataSource, selected.properties?.buildingID);
    return selected;
  }
  return null;
};

// Function to get the type of GeoJSON data
export const getGeojsonType = (geoJson) => {
  try {
    let data = geoJson;

    // If the input is a string, attempt to parse it as JSON
    if (typeof geoJson === "string") {
      try {
        data = JSON.parse(geoJson);
      } catch (error) {
        console.warn(error); // Log the error if parsing fails
        return null;
      }
    }

    // Validate the structure of the parsed GeoJSON
    if (!data || data.type !== "FeatureCollection" || !Array.isArray(data.features)) {
      return null; // Return null if the structure is not valid
    }

    // Check if the GeoJSON contains features with image properties
    const hasImageFeatures = data.features.some((feature) => {
      // Validate the structure of each feature
      if (!feature || feature.type !== "Feature" || !feature.properties) {
        return false;
      }

      const props = feature.properties;

      // Check if the feature has an array of images with the required properties
      return Array.isArray(props.images) && props.images.length > 0 && props.images.every((img) => img.hasOwnProperty("camera_type") && img.hasOwnProperty("relpath"));
    });

    // Return the appropriate type based on the presence of image features
    if (hasImageFeatures) {
      return Type.ASSET.GEOJSON_IMAGE;
    } else {
      return Type.ASSET.GEOJSON;
    }
  } catch (error) {
    console.log(error); // Log any unexpected errors
    return null; // Return null if an error occurs
  }
};

// Function to download GeoJSON data as a file
export const downloadGeoJSON = (data, fileName) => {
  // Clone the data to avoid mutating the original object
  const cloned = { ...data };

  // Stringify the features array with no indentation (1 line per feature)
  const compressedFeatures = data.features.map((f) => JSON.stringify(f));

  // Manually construct the final JSON string
  const jsonString =
    "{\n" +
    `  "type": "FeatureCollection",\n` +
    (cloned.name ? `  "name": ${JSON.stringify(cloned.name)},\n` : "") + // Optional property
    Object.entries(cloned)
      .filter(([key]) => key !== "type" && key !== "features" && key !== "name")
      .map(([key, value]) => `  "${key}": ${JSON.stringify(value)},`)
      .join("\n") +
    (compressedFeatures.length > 0 ? "\n" : "") +
    `  "features": [\n    ${compressedFeatures.join(",\n    ")}\n  ]\n` +
    "}";

  // Create a Blob from the JSON string
  const blob = new Blob([jsonString], { type: "application/json" });

  // Create a URL for the Blob
  const url = URL.createObjectURL(blob);

  // Create a temporary anchor to download
  const a = document.createElement("a");
  a.href = url;
  a.download = addExportedToFilename(fileName, "geojson");
  a.click();

  // Clean up
  URL.revokeObjectURL(url);
};

// Function to download KML data as a file
export const downloadKML = (kmlString, fileName) => {
  // Create a Blob from the KML string with the appropriate MIME type for KML files
  const blob = new Blob([kmlString], {
    type: "application/vnd.google-earth.kml+xml",
  });

  // Create a URL for the Blob object
  const url = URL.createObjectURL(blob);

  // Create a temporary anchor element to trigger the file download
  const a = document.createElement("a");
  a.href = url;

  // Generate a filename for the downloaded file by appending "kml" to the original filename
  a.download = addExportedToFilename(fileName, "kml");

  // Simulate a click on the anchor to initiate the download
  a.click();

  // Revoke the created object URL to release resources
  URL.revokeObjectURL(url);
};

// Function to add "_exported" to the filename before the extension
export const addExportedToFilename = (filename, ext) => {
  const epoch = Math.floor(Date.now() / 1000);

  const dotIndex = filename.lastIndexOf(".");
  let baseName = dotIndex === -1 ? filename : filename.substring(0, dotIndex);

  baseName = baseName.replace(/_\d+$/, "");

  return `${baseName}_${epoch}.${ext}`;
};

// Function to convert RGBA string to hex color code
export const rgbaToHex = (rgbaString) => {
  // Check if the input string is in the correct RGBA format
  if (!rgbaString.startsWith("rgba") || !rgbaString.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*(\d+(\.\d+)?)\)/)) {
    throw new Error("Invalid RGBA string format");
  }

  // Extract the RGBA components using a regular expression match
  const match = rgbaString.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*(\d+(\.\d+)?)\)/);
  const red = parseInt(match[1], 10); // Red component
  const green = parseInt(match[2], 10); // Green component
  const blue = parseInt(match[3], 10); // Blue component
  const alpha = parseFloat(match[4]); // Alpha component (opacity)

  // Convert RGB to hex (without alpha)
  const hex = `#${((1 << 24) + (red << 16) + (green << 8) + blue).toString(16).slice(1)}`;

  // If alpha is less than 1, add it as part of the hex string
  if (alpha < 1) {
    const alphaHex = Math.round(alpha * 255) // Convert alpha to a value between 0 and 255
      .toString(16) // Convert to hex
      .padStart(2, "0"); // Ensure it's 2 digits long
    return `${hex}${alphaHex}`; // Return hex with alpha
  }

  // Return the hex without alpha if alpha is 1
  return hex;
};

// Function to remove the file extension from a filename
export const removeFileExtension = (filename) => {
  // Find the last occurrence of the dot (.) in the filename
  if (filename) {
    const lastDotIndex = filename.lastIndexOf(".");

    // If there's no dot, it means there's no extension, so return the original filename
    if (lastDotIndex === -1) {
      return filename;
    }

    // Extract the filename without the extension
    return filename.substring(0, lastDotIndex);
  }
  return "";
};

export const removeEpochSuffix = (str) => {
  if (typeof str !== "string") return str;

  const m = str.match(/^(.*)_(17\d{8})(\.[^.]+)?$/);
  if (!m) return str;

  const [, prefix, ts, ext = ""] = m;

  const sec = Number(ts);
  if (!Number.isFinite(sec)) return str;

  const MIN = Math.floor(Date.UTC(2017, 0, 1) / 1000);
  const MAX = Math.floor(Date.UTC(2100, 0, 1) / 1000);
  if (sec < MIN || sec > MAX) return str;

  return `${prefix}${ext}`;
};
export const hasEpochSuffix = (str) => {
  if (typeof str !== "string") return false;

  const m = str.match(/^(.*)_(17\d{8})(\.[^.]+)?$/);
  if (!m) return false;

  const sec = Number(m[2]);
  if (!Number.isFinite(sec)) return false;

  const MIN = Math.floor(Date.UTC(2017, 0, 1) / 1000);
  const MAX = Math.floor(Date.UTC(2100, 0, 1) / 1000);

  return sec >= MIN && sec <= MAX;
};

// Function to convert GeoJSON data to KML format
export const convertGeoJsonToKml = (geojson, options = {}) => {
  try {
    // Basic input check
    if (!geojson || typeof geojson !== "object") throw new Error("Invalid GeoJSON");

    // Convert using tokml. name/description map to property keys.
    const kmlString = toKML(geojson, {
      name: options.nameKey || "name", // property key for Placemark name
      description: options.descriptionKey || "desc", // property key for description
      documentName: options.documentName, // optional document name
      documentDescription: options.documentDescription, // optional document description
    });

    return kmlString;
  } catch (err) {
    // Log error and return null
    console.error(err);
    return null;
  }
};

// Function to filter entities in a data source based on a specific property and value
export const filterEntitiesByProperty = async (viewer, dataSource, filters = []) => {
  const entities = dataSource.entities.values;
  const matchingEntities = [];

  const tryParseNumber = (val) => {
    const num = Number(val);
    return isNaN(num) ? val : num;
  };

  const compare = (a, b, operator) => {
    a = tryParseNumber(a);
    b = tryParseNumber(b);

    switch (operator) {
      case "==":
        return a == b;
      case "!=":
        return a != b;
      case "<":
        return a < b;
      case "<=":
        return a <= b;
      case ">":
        return a > b;
      case ">=":
        return a >= b;
      default:
        return false;
    }
  };

  for (const entity of entities) {
    const props = entity.properties;
    let match = false;

    if (props) {
      match = filters.every(({ key1, key2, value, operation }) => {
        const group = props[key1];
        if (!group) return false;

        const rawGroup = typeof group.getValue === "function" ? group.getValue() : group;

        if (key1 === "relation") {
          // Simple key-value object
          const val = rawGroup[key2];
          return compare(val, value, operation);
        }

        if (key1 === "way" || key1 === "node") {
          // It's an array of { id, tags }
          if (!Array.isArray(rawGroup)) return false;

          // At least one item in the array should match the condition
          return rawGroup.some((item) => {
            const val = item?.tags?.[key2];
            return compare(val, value, operation);
          });
        }

        return false; // fallback
      });
    }

    entity.show = match;
    if (match) matchingEntities.push(entity);
  }

  if (matchingEntities.length > 0) {
    await viewer.zoomTo(matchingEntities);
  }
};

export const resetEntityFilter = (dataSource) => {
  const entities = dataSource.entities.values; // Get all entities from the data source
  for (const entity of entities) {
    entity.show = true; // Set all entities to be visible
  }
};

export const getGeometryTypeGeojson = (geojson) => {
  if (!geojson) return null;
  // Check if the GeoJSON is a string and parse it to an object if needed
  if (typeof geojson === "string") {
    geojson = JSON.parse(geojson);
  }

  let geomType = null;

  // Handle cases based on GeoJSON type
  if (geojson.type === "Feature") {
    geomType = geojson.geometry.type; // Get geometry type from a Feature
  } else if (geojson.type === "FeatureCollection") {
    geomType = geojson?.features[0]?.geometry.type; // Get geometry type from the first feature in a FeatureCollection
  } else if (geojson.type && geojson.coordinates) {
    geomType = geojson.type; // Get geometry type directly if it's a simple geometry object
  } else {
    throw new Error("Unsupported GeoJSON format"); // Throw an error if the format is unsupported
  }

  return geomType; // Return the geometry type
};

export const getGeometryTypeEntity = (entity) => {
  if (!entity) return null;

  // Cesium entity properties → check which geometry is defined
  if (entity.point) {
    return "Point";
  } else if (entity.polyline) {
    return "LineString";
  } else if (entity.polygon) {
    return "Polygon";
  }

  // fallback for unsupported or composite entities
  return null;
};

export const getBoundingBox = (viewer) => {
  const scene = viewer.scene;
  const ellipsoid = scene.globe.ellipsoid;

  const corners = [
    new Cartesian2(0, 0), // top-left
    new Cartesian2(scene.canvas.clientWidth, 0), // top-right
    new Cartesian2(scene.canvas.clientWidth, scene.canvas.clientHeight), // bottom-right
    new Cartesian2(0, scene.canvas.clientHeight), // bottom-left
  ];

  const cartographicCorners = corners.map((corner) => {
    const ray = scene.camera.getPickRay(corner);
    const position = scene.globe.pick(ray, scene);
    if (!position) return null;
    return ellipsoid.cartesianToCartographic(position);
  });

  const validCorners = cartographicCorners.filter((c) => c !== null);

  if (validCorners.length < 4) {
    return null;
  }

  const formattedCorners = validCorners.map((c) => {
    const lat = CesiumMath.toDegrees(c.latitude);
    const lon = CesiumMath.toDegrees(c.longitude);
    return [lat, lon];
  });

  formattedCorners.push(formattedCorners[0]);

  return formattedCorners;
};

export const saveToCache = (key, data) => {
  // Store the data in localStorage under the specified key after converting it to a JSON string
  localStorage.setItem(key, JSON.stringify(data));
};

export const removeToCache = (key) => {
  // Store the data in localStorage under the specified key after converting it to a JSON string
  localStorage.removeItem(key);
};

export const getFromCache = (key) => {
  // Retrieve data from localStorage for the given key and parse it as JSON, or return null if not found
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : null;
};

export const formatToISO = (dateStr, timeStr, timeZoneOffsetMinutes = new Date().getTimezoneOffset()) => {
  // Split the date and time strings into their respective components
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hour, minute] = timeStr.split(":").map(Number);

  // Create a Date object in UTC
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute));

  // Calculate the timezone offset in hours and minutes
  const offsetHours = Math.floor(Math.abs(timeZoneOffsetMinutes) / 60);
  const offsetMinutes = Math.abs(timeZoneOffsetMinutes) % 60;
  const offsetSign = timeZoneOffsetMinutes > 0 ? "-" : "+";
  const offset = `${offsetSign}${String(offsetHours).padStart(2, "0")}:${String(offsetMinutes).padStart(2, "0")}`;

  // Adjust the date based on the timezone offset
  const localDate = new Date(date.getTime() - timeZoneOffsetMinutes * 60 * 1000);

  // Return the formatted ISO string with the correct timezone offset
  const isoString = localDate.toISOString().slice(0, -1);
  return `${isoString}${offset}`;
};

export const formatNumberForProgress = (num) => {
  // Check if the number is greater than 1000
  if (num) {
    if (num > 1000) {
      // Convert the number to a string, divide by 1000, and format with one decimal place
      return (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
    }
    // If the number is not greater than 1000, just return it as a string
    return num.toString();
  } else {
    return 0;
  }
};

export const capitalizeFirstLetter = (str) => {
  if (typeof str !== "string" || str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const manageViewerCursorAppearance = (viewer, cursorModeRef) => {
  if (!viewer || !viewer.scene || !viewer.scene.canvas) {
    console.warn("Invalid Cesium viewer provided.");
    return;
  }

  const canvas = viewer.scene.canvas;
  const handler = new ScreenSpaceEventHandler(canvas);

  handler.setInputAction((movement) => {
    const pickedObject = viewer.scene.pick(movement.endPosition);
    const mode = cursorModeRef.current;

    switch (mode) {
      case Type.CURSOR.DEFAULT:
        canvas.style.cursor = pickedObject && pickedObject.id ? "pointer" : "default";
        break;
      case Type.CURSOR.PLUS:
        canvas.style.cursor = pickedObject && pickedObject.id ? "pointer" : "crosshair";
        break;
      case Type.CURSOR.GRAB:
        canvas.style.cursor = pickedObject && pickedObject.id ? "pointer" : "grab";
        break;
      case Type.CURSOR.CUT:
        canvas.style.cursor = pickedObject && pickedObject.id ? "pointer" : "cell";
        break;
      default:
        canvas.style.cursor = "default";
    }
  }, ScreenSpaceEventType.MOUSE_MOVE);

  return handler;
};

export const getDataSourceGeometryType = (dataSource) => {
  if (!dataSource || !dataSource.entities) return "empty";

  const entities = dataSource.entities.values;
  if (entities.length === 0) return "empty";

  let hasPoint = false;
  let hasLine = false;
  let hasPolygon = false;

  for (const entity of entities) {
    if (entity.point) hasPoint = true;
    if (entity.polyline) hasLine = true;
    if (entity.polygon) hasPolygon = true;
  }

  const types = [hasPoint, hasLine, hasPolygon].filter(Boolean).length;

  if (types === 0) return "empty";
  if (types > 1) return "mixed";
  if (hasPoint) return "point";
  if (hasLine) return "line";
  if (hasPolygon) return "polygon";

  return "empty";
};

export const truncateWords = (text, count) => {
  if (!text) return "";
  const words = text.split(" ");
  if (words.length <= count) return text;
  return words.slice(0, count).join(" ") + " …";
};

export const matchKeyInText = (key, text) => {
  const normalize = (str) => str.toLowerCase().replace(/[\s_\-]/g, "");

  const normalizedKey = normalize(key);
  const normalizedText = normalize(text);

  return normalizedText.includes(normalizedKey) || normalizedKey.includes(normalizedText);
};

export const mergeWithFallback = (obj1, obj2) => {
  const merged = {};

  const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

  allKeys.forEach((key) => {
    merged[key] = {
      ...(obj1[key] || {}),
      ...(obj2[key] || {}),
    };
  });

  return merged;
};

export const cloneEntity = (entity) => {
  const now = JulianDate.now();

  let clonedPolyline = undefined;
  if (entity.polyline) {
    const positions = entity.polyline.positions?.getValue(now) || [];
    const copiedPositions = positions.map((pos) => Cartesian3.clone(pos));

    clonedPolyline = {
      positions: new ConstantProperty(copiedPositions),
      width: new ConstantProperty(entity.polyline.width?.getValue(now)),
      material: entity.polyline.material?.clone?.() ?? entity.polyline.material,
      clampToGround: entity.polyline.clampToGround,
    };
  }

  let clonedPolygon = undefined;
  if (entity.polygon) {
    const hierarchy = entity.polygon.hierarchy?.getValue(now);
    const copiedPositions = hierarchy?.positions?.map((pos) => Cartesian3.clone(pos)) || [];

    clonedPolygon = {
      hierarchy: new ConstantProperty(new PolygonHierarchy(copiedPositions)),
      material: entity.polygon.material,
      outline: entity.polygon.outline,
      outlineColor: entity.polygon.outlineColor,
    };
  }

  const positionValue = entity.position?.getValue?.(now);
  const clonedPosition = positionValue ? new ConstantProperty(Cartesian3.clone(positionValue)) : undefined;

  const e = new Entity({
    id: entity.id,
    properties: entity.properties?.clone?.() ?? entity.properties,
    polyline: clonedPolyline,
    polygon: clonedPolygon,
    position: clonedPosition,
  });

  e.geojsonType = entity.geojsonType;
  return e;
};

export const createFillPattern2 = (imagePath, repeatX = 1, repeatY = 1) => {
  if (!imagePath) return null;
  return new ImageMaterialProperty({
    image: imagePath,
    repeat: new Cartesian2(repeatX, repeatY),
    transparent: true,
  });
};
/**
 * Create an ImageMaterialProperty from an SVG and recolor it (UTF-8 safe version).
 * Automatically tiles the pattern across the polygon.
 */
/**
 * Create an ImageMaterialProperty from an SVG and recolor its visible shapes.
 * Works with <path>, <rect>, and <circle> elements.
 */
const _patternCache = new Map();

export const createFillPattern = async (imagePath, color = "#611e1eff") => {
  if (!imagePath) return null;

  // use key = path + color
  const key = `${imagePath}|${color}`;
  if (_patternCache.has(key)) return _patternCache.get(key); // return cached

  try {
    // load SVG
    const response = await fetch(imagePath);
    let svgText = await response.text();

    // recolor
    svgText = svgText
      .replace(/<path([^>]*)fill="[^"]*"([^>]*)>/g, `<path$1fill="${color}"$2>`)
      .replace(/<rect([^>]*)fill="[^"]*"([^>]*)>/g, `<rect$1fill="${color}"$2>`)
      .replace(/<circle([^>]*)fill="[^"]*"([^>]*)>/g, `<circle$1fill="${color}"$2>`);

    // encode base64
    const utf8Svg = new TextEncoder().encode(svgText);
    const svgBase64 = `data:image/svg+xml;base64,${btoa(
      Array.from(utf8Svg)
        .map((b) => String.fromCharCode(b))
        .join(""),
    )}`;

    // make material
    const mat = new ImageMaterialProperty({
      image: svgBase64,
      repeat: new Cartesian2(20, 20),
      transparent: true,
    });

    _patternCache.set(key, mat); // cache result
    return mat;
  } catch (err) {
    console.warn("Pattern creation failed:", err);
    return null;
  }
};
export const getCurrentViewBoundingBox = (viewer) => {
  if (!viewer || !viewer.scene) return null;

  const scene = viewer.scene;
  const canvas = scene.canvas;

  const topLeft = new Cartesian2(0, 0);
  const bottomRight = new Cartesian2(canvas.width, canvas.height);

  const ellipsoid = scene.globe.ellipsoid;

  const topLeftCarto = ellipsoid.cartesianToCartographic(scene.camera.pickEllipsoid(topLeft, ellipsoid));
  const bottomRightCarto = ellipsoid.cartesianToCartographic(scene.camera.pickEllipsoid(bottomRight, ellipsoid));

  if (!topLeftCarto || !bottomRightCarto) return null;

  const west = CesiumMath.toDegrees(topLeftCarto.longitude);
  const north = CesiumMath.toDegrees(topLeftCarto.latitude);
  const east = CesiumMath.toDegrees(bottomRightCarto.longitude);
  const south = CesiumMath.toDegrees(bottomRightCarto.latitude);

  return { west, south, east, north };
};

export const bboxToCoordinates = (bbox, type = "object") => {
  if (!bbox) return null;

  const { west, south, east, north } = bbox;

  const coordsArray = [
    [west, south], // bottom-left
    [east, south], // bottom-right
    [east, north], // top-right
    [west, north], // top-left
    [west, south], // close ring
  ];

  if (type === "array") {
    return coordsArray;
  }

  // default → object format
  return coordsArray.map(([lon, lat]) => ({ lon, lat }));
};

export const onMapMove = (viewer, callback) => {
  if (!viewer || !viewer.camera) {
    return;
  }

  // Remove old listener if already set (optional)
  if (viewer._MovingEndListener) {
    viewer.camera.moveEnd.removeEventListener(viewer._MovingEndListener);
  }

  // Store and add
  viewer._MovingEndListener = callback;
  viewer.camera.moveEnd.addEventListener(callback);
};
/**
 * createGeoJSONFile v2
 * Creates a File from GeoJSON with optional "one feature per line" output.
 * - If oneFeaturePerLine=true: keeps a valid FeatureCollection; each feature is single-line inside the array.
 * - If ndjson=true: writes one Feature per line (NDJSON). Great for streaming/CLI tools.
 */
export const createGeoJSONFile = (geojson, layerName, e = null, options = { oneFeaturePerLine: true, ndjson: false }) => {
  try {
    // Normalize options with defaults
    const { oneFeaturePerLine = true, ndjson = false } = options || {};

    // Ensure we have an object (if string, parse; otherwise use as-is)
    const obj = typeof geojson === "string" ? JSON.parse(geojson) : geojson;

    // Epoch seconds for filename
    const epochSeconds = e == null ? Math.floor(Date.now() / 1000) : e;

    // Safe filename (remove spaces & unsafe chars)
    const safeLayerName = String(layerName || "layer")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^A-Za-z0-9._-]/g, "_");

    // --- Build payload string ------------------------------------------------
    let payload = "";
    let mime = "application/geo+json";
    let ext = "geojson";

    // If user wants NDJSON (one Feature per line, each a standalone JSON)
    if (ndjson) {
      // Validate input is a FeatureCollection
      if (obj?.type !== "FeatureCollection" || !Array.isArray(obj?.features)) {
        throw new Error("NDJSON mode requires a FeatureCollection with features[].");
      }
      // Join each feature on its own line
      payload = obj.features.map((f) => JSON.stringify(f)).join("\n");
      mime = "application/x-ndjson";
      ext = "ndjson"; // or "geojsonl" if you prefer
    } else if (oneFeaturePerLine && obj?.type === "FeatureCollection" && Array.isArray(obj?.features)) {
      // Keep a valid FeatureCollection, but print features as single-line entries
      // Build string manually to control newlines precisely
      const header = `{"type":"FeatureCollection","features":[\n`;
      const body = obj.features.map((f) => JSON.stringify(f)).join(",\n");
      const footer = `\n]}`;
      payload = header + body + footer;
      mime = "application/geo+json";
      ext = "geojson";
    } else {
      // Fallback: regular JSON.stringify with no extra spaces
      payload = JSON.stringify(obj);
      mime = "application/geo+json";
      ext = "geojson";
    }

    // Final filename
    const fileName = `${safeLayerName}_${epochSeconds}.${ext}`;

    // Create File
    return new File([payload], fileName, { type: mime });
  } catch (err) {
    console.error("createGeoJSONFile error:", err);
    return null;
  }
};

export const bboxCornersLonLatArrays = (geojsonInput) => {
  // --- Accept object or JSON string ---
  const geojson = typeof geojsonInput === "string" ? JSON.parse(geojsonInput) : geojsonInput;

  // --- Track min/max for latitude and longitude ---
  let minLat = Infinity,
    minLon = Infinity,
    maxLat = -Infinity,
    maxLon = -Infinity;

  /**
   * visitCoord
   * Updates bbox trackers for one coordinate [lon, lat].
   */
  function visitCoord(coord) {
    if (!Array.isArray(coord) || coord.length < 2) return;

    const lon = Number(coord[0]);
    const lat = Number(coord[1]);

    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;

    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  }

  /**
   * walkGeometry
   * Recursively walks any GeoJSON geometry.
   */
  function walkGeometry(geometry) {
    if (!geometry) return;
    const { type, coordinates, geometries } = geometry;

    switch (type) {
      case "Point":
        visitCoord(coordinates);
        break;
      case "MultiPoint":
      case "LineString":
        for (const c of coordinates) visitCoord(c);
        break;
      case "MultiLineString":
      case "Polygon":
        for (const part of coordinates) {
          for (const c of part) visitCoord(c);
        }
        break;
      case "MultiPolygon":
        for (const poly of coordinates) {
          for (const ring of poly) {
            for (const c of ring) visitCoord(c);
          }
        }
        break;
      case "GeometryCollection":
        for (const g of geometries || []) walkGeometry(g);
        break;
    }
  }

  /**
   * processRoot
   * Handles Feature, FeatureCollection, or bare Geometry roots.
   */
  function processRoot(root) {
    if (!root) return;
    if (root.type === "Feature") {
      walkGeometry(root.geometry);
    } else if (root.type === "FeatureCollection") {
      for (const f of root.features || []) walkGeometry(f.geometry);
    } else {
      walkGeometry(root);
    }
  }

  // --- Kick off traversal ---
  processRoot(geojson);

  if (minLat === Infinity || minLon === Infinity || maxLat === -Infinity || maxLon === -Infinity) {
    showToast("No valid coordinates found in the provided GeoJSON.", Type.NOTIFICATION.ERROR);
    // throw new Error("No valid coordinates found in the provided GeoJSON.");
    return;
  }

  // --- Return bbox corners as [lon, lat] arrays ---
  return [
    [minLon, minLat], // SW
    [maxLon, minLat], // SE
    [maxLon, maxLat], // NE
    [minLon, maxLat], // NW
    [minLon, minLat], // SW (close)
  ];
};

// Function: Calculate the combined bounding box of a list of GeoJSON objects
// If no coordinates exist (empty input), return an empty array.
export const getCombinedBBox = (geojsonList) => {
  let minLat = Infinity,
    minLon = Infinity,
    maxLat = -Infinity,
    maxLon = -Infinity;

  // Helper: visit each coordinate
  function visitCoord(coord) {
    if (!Array.isArray(coord) || coord.length < 2) return;
    const lon = Number(coord[0]); // longitude
    const lat = Number(coord[1]); // latitude
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;

    // Update bounding values
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  }

  // Walk through geometry recursively
  function walkGeometry(geometry) {
    if (!geometry) return;
    const { type, coordinates, geometries } = geometry;
    switch (type) {
      case "Point":
        visitCoord(coordinates);
        break;
      case "MultiPoint":
      case "LineString":
        coordinates.forEach(visitCoord);
        break;
      case "MultiLineString":
      case "Polygon":
        coordinates.forEach((part) => part.forEach(visitCoord));
        break;
      case "MultiPolygon":
        coordinates.forEach((poly) => poly.forEach((ring) => ring.forEach(visitCoord)));
        break;
      case "GeometryCollection":
        (geometries || []).forEach(walkGeometry);
        break;
    }
  }

  // Process root GeoJSON
  function processRoot(root) {
    if (!root) return;
    if (root.type === "Feature") {
      walkGeometry(root.geometry);
    } else if (root.type === "FeatureCollection") {
      (root.features || []).forEach((f) => walkGeometry(f.geometry));
    } else {
      walkGeometry(root); // assume geometry
    }
  }

  // Iterate over all geojsons
  for (const g of geojsonList) {
    const obj = typeof g === "string" ? JSON.parse(g) : g;
    processRoot(obj);
  }

  // If no coordinates found, return empty array instead of throwing error
  if (minLat === Infinity || minLon === Infinity || maxLat === -Infinity || maxLon === -Infinity) {
    return [];
  }

  // Return bbox as array of arrays in [lat, lon]
  return [
    [minLat, minLon], // SW
    [minLat, maxLon], // SE
    [maxLat, maxLon], // NE
    [maxLat, minLon], // NW
    [minLat, minLon], // close ring
  ];
};

export const formatToTitleCase = (str) => {
  return str
    .split("_") // split by underscore
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize each word
    .join(" "); // join back with spaces
};

export const getEntityBBox = (entity, time = JulianDate.now()) => {
  let positions;

  // Handle Polygon
  if (entity.polygon) {
    positions = entity.polygon.hierarchy.getValue(time).positions;
  }
  // Handle Polyline
  else if (entity.polyline) {
    positions = entity.polyline.positions.getValue(time);
  }
  // Handle Point
  else if (entity.position) {
    positions = [entity.position.getValue(time)];
  }

  if (!positions || positions.length === 0) return null;

  // Convert cartesian to cartographic (lon/lat)
  const cartographics = positions.map((p) => Cartographic.fromCartesian(p));

  // Extract mins and maxes
  const lons = cartographics.map((c) => CesiumMath.toDegrees(c.longitude));
  const lats = cartographics.map((c) => CesiumMath.toDegrees(c.latitude));

  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);

  // Return rectangle polygon (5 points, closed)
  return [
    [minLon, minLat], // bottom-left
    [maxLon, minLat], // bottom-right
    [maxLon, maxLat], // top-right
    [minLon, maxLat], // top-left
    [minLon, minLat], // close polygon
  ];
};

export const swapLatLon = (coords) => {
  // Map over each coordinate and swap index 0 and 1
  return coords.map(([lat, lon]) => [lon, lat]);
};

// Keep this util
export const showToast = (text, type = Type.NOTIFICATION.INFO, options = {}) => {
  // extract known fields
  const {
    closable = true, // show Close button or not
    action, // { label, onClick }
    meta = {}, // custom meta
    ...rest // any other toast props (title, duration, etc.)
  } = options;

  queueMicrotask(() => {
    toaster.create({
      description: text,
      type,
      action, // Chakra will pass this to Toast.ActionTrigger
      meta: {
        // put flags in meta for your renderer
        closable,
        ...meta,
      },
      overlap: true,
      closable: undefined, // prevent accidental top-level usage
      ...rest,
    });
  });
};

export const safeGetById = (entities, id) => {
  if (!entities) return undefined;
  if (id === undefined || id === null) return undefined;
  return entities.getById(String(id));
};

export const floatingStyles = defineStyle({
  pos: "absolute",
  bg: "bg",
  px: "0.5",
  top: "-3",
  insetStart: "2",
  fontWeight: "normal",
  pointerEvents: "none",
  transition: "position",
  _peerPlaceholderShown: {
    color: "fg.muted",
    top: "2.5",
    insetStart: "3",
  },
  _peerFocusVisible: {
    color: "fg",
    top: "-3",
    insetStart: "2",
  },
});

export function getFileTypeFromUrl(url) {
  // Normalize URL to lowercase
  if (!url) return "unknown";
  const lowerUrl = url.toLowerCase();

  // Common image extensions
  const imageExts = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".svg"];
  // Common video extensions
  const videoExts = [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"];

  // Check if URL ends with one of the extensions
  if (imageExts.some((ext) => lowerUrl.endsWith(ext))) {
    return "image";
  }
  if (videoExts.some((ext) => lowerUrl.endsWith(ext))) {
    return "video";
  }
  return "unknown";
}

export const ReviewTitleOptions = ({ collection }) => {
  return collection.items.map((item, index) => (
    <Box key={index}>
      <Text fontWeight={"bold"}>{formatToTitleCase(item.category)}</Text>
      {item.options.map((option) => (
        <Select.Item item={option} key={option.value} pl={5}>
          {option.label}
          <Select.ItemIndicator />
        </Select.Item>
      ))}
    </Box>
  ));
};

export async function setCameraAboveGround(viewer, metersAboveGround, orientation) {
  // Get current camera geodetic lon/lat/height (in radians/meters)
  const carto = viewer.camera.positionCartographic;
  const lon = carto.longitude; // radians
  const lat = carto.latitude; // radians

  // Prepare a Cartographic at same lon/lat to query ground height
  const groundCarto = new Cartographic(lon, lat, 0);

  // Determine ground height in meters above ellipsoid
  let groundHeight = 0;

  try {
    // Prefer the most detailed scene height if available (terrain + tiles)
    if (viewer.scene.sampleHeightMostDetailed) {
      const h = await viewer.scene.sampleHeightMostDetailed(groundCarto);
      if (defined(h)) {
        groundHeight = h; // meters
      } else {
        // Fallback to globe height (may be less detailed)
        groundHeight = viewer.scene.globe.getHeight(groundCarto) ?? 0;
      }
    } else {
      // Older Cesium versions: fallback directly
      groundHeight = viewer.scene.globe.getHeight(groundCarto) ?? 0;
    }
  } catch (e) {
    // Final fallback: assume ellipsoid surface (0)
    groundHeight = viewer.scene.globe.getHeight(groundCarto) ?? 0;
  }

  // Target absolute height above ellipsoid = ground + offset
  const targetHeight = groundHeight + metersAboveGround;

  // Convert back to Cartesian for camera destination
  const destination = Cartesian3.fromRadians(lon, lat, targetHeight);

  // Apply camera view instantly (use flyTo for animation if desired)
  viewer.camera.setView({
    destination,
    orientation: {
      heading: orientation.heading,
      pitch: orientation.pitch,
      roll: orientation.roll,
    },
  });

  // Tip: for animation, replace with:
  // viewer.camera.flyTo({ destination, orientation, duration: 0.8 });
}

export async function setHumanView(viewer) {
  const heading = viewer.camera.heading; // keep current facing direction
  await setCameraAboveGround(viewer, 2.5, {
    heading,
    pitch: 0, // 0 rad = look at the horizon
    roll: 0, // keep horizon level
  });
}

export async function setTopDownView(viewer) {
  const heading = viewer.camera.heading; // optional: set to 0 if you prefer
  await setCameraAboveGround(viewer, 500, {
    heading,
    pitch: -CesiumMath.PI_OVER_TWO, // -90° = straight down
    roll: 0, // keep horizon level
  });
}

export const rightClickOnPointClouds = (viewer) => {
  // Prevent default context menu
  viewer.scene.canvas.addEventListener("contextmenu", (e) => e.preventDefault());

  const popupId = "cesium-rc-popup";
  let popup = document.getElementById(popupId);

  if (!popup) {
    popup = document.createElement("div");
    popup.id = popupId;

    // Popup container styles
    Object.assign(popup.style, {
      position: "absolute",
      zIndex: 9999,
      minWidth: "160px",
      padding: "8px",
      borderRadius: "10px",
      boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
      background: "#ffffff",
      color: "#111827",
      fontFamily: "sans-serif",
      fontSize: "13px",
      display: "none",
      userSelect: "none",
    });

    const title = document.createElement("div");
    Object.assign(title.style, { fontWeight: 600, marginBottom: "6px", opacity: 0.9 });
    title.textContent = "View Options";
    popup.appendChild(title);

    // Helper to make a white button
    const makeBtn = (label) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = label;
      Object.assign(btn.style, {
        width: "100%",
        padding: "8px",
        margin: "4px 0",
        borderRadius: "8px",
        border: "1px solid rgba(0,0,0,0.12)",
        background: "#ffffff",
        color: "#111827",
        cursor: "pointer",
        transition: "background 120ms ease, transform 60ms ease",
      });
      btn.onmouseenter = () => (btn.style.background = "#f3f4f6");
      btn.onmouseleave = () => (btn.style.background = "#ffffff");
      btn.onmousedown = () => (btn.style.transform = "scale(0.99)");
      btn.onmouseup = () => (btn.style.transform = "scale(1)");
      return btn;
    };

    const streetBtn = makeBtn("Street View");
    const humanBtn = makeBtn("Truck View (+2.5 m)");
    const topDownBtn = makeBtn("Top Down View (+300 m)");

    popup.appendChild(streetBtn);
    popup.appendChild(humanBtn);
    popup.appendChild(topDownBtn);
    document.body.appendChild(popup);

    popup._streetBtn = streetBtn;
    popup._humanBtn = humanBtn;
    popup._topDownBtn = topDownBtn;
  }

  // Show/hide helpers
  const showPopupAt = (x, y) => {
    const offset = 6;
    popup.style.left = `${x + offset}px`;
    popup.style.top = `${y + offset}px`;
    popup.style.display = "block";
  };
  const hidePopup = () => (popup.style.display = "none");

  // Close on outside click or ESC
  const onDocClick = (e) => {
    if (!popup.contains(e.target)) hidePopup();
  };
  const onKeyDown = (e) => {
    if (e.key === "Escape") hidePopup();
  };
  document.addEventListener("mousedown", onDocClick);
  document.addEventListener("keydown", onKeyDown);

  // Last clicked info
  let lastClicked = null; // { lon, lat, height, headingAtClick }

  // Camera goto: keep heading, look at horizon (pitch = 0), roll = 0
  const goTo = ({ lon, lat, height, heading, pitch = 0.0 }) => {
    const roll = 0.0;
    viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(lon, lat, height),
      orientation: { heading, pitch, roll },
      duration: 1.5,
      maximumHeight: height + 150.0,
    });
  };

  // Buttons actions
  popup._streetBtn.onclick = () => {
    if (!lastClicked) return hidePopup();
    const { lon, lat, height, headingAtClick } = lastClicked;
    goTo({ lon, lat, height, heading: headingAtClick, pitch: 0.0 });
    hidePopup();
  };

  popup._humanBtn.onclick = () => {
    if (!lastClicked) return hidePopup();
    const { lon, lat, height, headingAtClick } = lastClicked;
    goTo({ lon, lat, height: height + 2.5, heading: headingAtClick, pitch: 0.0 });
    hidePopup();
  };

  popup._topDownBtn.onclick = () => {
    if (!lastClicked) return hidePopup();
    const { lon, lat, height } = lastClicked;
    viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(lon, lat, height + 300.0),
      orientation: {
        heading: 0.0, // North
        pitch: CesiumMath.toRadians(-90), // Look straight down
        roll: 0.0,
      },
      duration: 1.5,
      maximumHeight: height + 600.0,
    });
    hidePopup();
  };

  // Right-click handler
  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

  handler.setInputAction((movement) => {
    const picked = viewer.scene.pick(movement.position);

    if (defined(picked) && picked.primitive instanceof Cesium3DTileset) {
      const cartesian = viewer.scene.pickPosition(movement.position);

      if (defined(cartesian)) {
        const cartographic = Cartographic.fromCartesian(cartesian);
        const lon = CesiumMath.toDegrees(cartographic.longitude);
        const lat = CesiumMath.toDegrees(cartographic.latitude);
        const height = cartographic.height;

        const headingAtClick = viewer.camera.heading;

        lastClicked = { lon, lat, height, headingAtClick };
        showPopupAt(movement.position.x, movement.position.y);
      } else {
        hidePopup();
      }
    } else {
      hidePopup();
    }
  }, ScreenSpaceEventType.RIGHT_CLICK);

  return {
    destroy: () => {
      try {
        handler && handler.destroy();
      } catch {}
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
      hidePopup();
    },
  };
};

export function formatISODateSimple(isoString) {
  try {
    const [datePart, timePart] = isoString.split("T");
    if (!datePart || !timePart) return isoString;

    const cleanTime = timePart.split(".")[0].replace("Z", "");

    return `${datePart} ${cleanTime}`;
  } catch {
    return isoString;
  }
}

export function normalizeGeometryType(input) {
  const validTypes = ["Point", "MultiPoint", "LineString", "MultiLineString", "Polygon", "MultiPolygon"];

  const lowerInput = input.toLowerCase();

  const matchedType = validTypes.find((type) => type.toLowerCase() === lowerInput);

  return matchedType || null;
}

export const insertByPriority = (list, item) => {
  // Normalize priority: default to 0 if missing/invalid
  const itemPriority = Number.isFinite(Number(item.priority)) ? Number(item.priority) : 0;

  // Stable tie-breaker: earlier items stay "lower" if same priority
  if (typeof item._priorityOrder !== "number") {
    item._priorityOrder = typeof performance !== "undefined" ? performance.now() : Date.now();
  }

  // Find correct insertion index:
  // - If priority equal, sort by _priorityOrder ASC (older first)
  let idx = list.length; // default append at end
  for (let i = 0; i < list.length; i++) {
    const cur = list[i];
    const curPriority = Number.isFinite(Number(cur.priority)) ? Number(cur.priority) : 0;
    const curOrder = Number.isFinite(Number(cur._priorityOrder)) ? Number(cur._priorityOrder) : 0;

    if (curPriority < itemPriority) {
      idx = i;
      break;
    } else if (curPriority === itemPriority) {
      if (curOrder > item._priorityOrder) {
        idx = i;
        break;
      }
    }
  }

  // Return a NEW array with the item inserted
  const out = list.slice(0, idx);
  out.push(item);
  return out.concat(list.slice(idx));
};

export const readAsText = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result);
    r.onerror = reject;
    r.readAsText(file);
  });

export const readAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result);
    r.onerror = reject;
    r.readAsArrayBuffer(file);
  });

// Utility.js
export function reorderDataSourcesByPriority(viewer) {
  if (!viewer) return;
  // collect current datasources + priorities
  const items = viewer.dataSources._dataSources.slice().map((ds, i) => ({
    ds,
    p: Number.isFinite(ds._priority) ? ds._priority : 0,
    t: Number.isFinite(ds._priorityOrder) ? ds._priorityOrder : i, // tie-breaker
  }));
  // small -> big, last raiseToTop => highest priority ends on top
  items.sort((a, b) => a.p - b.p || a.t - b.t);
  items.forEach(({ ds }) => {
    viewer.dataSources.raiseToTop(ds);
  });
}

export function buildCode(str) {
  if (typeof str !== "string") return "";
  const words = str.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const firstTwo = words[0].slice(0, 2);
  const initials = words
    .slice(1)
    .map((w) => w[0] || "")
    .join("");
  return (firstTwo + initials).toUpperCase();
}

// Returns true if b exists in a after normalizing.
// Normalize: lowercase and remove spaces, hyphens, and underscores.
export function includesNormalized(a, b) {
  const norm = (s) =>
    String(s)
      .toLowerCase()
      .replace(/[ \-_]/g, "");
  return norm(a).includes(norm(b));
}

export const deleteAssetsFromAsseest = (viewer, item, setMapObjectsAssest, setLoadedPorject, deleteAll = false) => {
  // update map objects
  setMapObjectsAssest((prev) => {
    const ds = viewer?.dataSources;

    if (deleteAll) {
      if (ds) {
        prev.forEach((obj) => {
          if (obj?.dataSource) ds.remove(obj.dataSource);
        });
      }
      return [];
    }

    // remove ALL items with project_id === item.id
    if (ds) {
      prev
        .filter((obj) => obj?.project_id === item?.id)
        .forEach((obj) => {
          if (obj?.dataSource) ds.remove(obj.dataSource);
        });
    }

    return prev.filter((obj) => obj?.project_id !== item?.id);
  });

  // update loaded projects
  setLoadedPorject((prev) => {
    if (deleteAll) return [];
    return prev.filter((p) => p?.id !== item?.id);
  });
};

export const removeEntities = (viewer, entities, setMapObjects) => {
  const selectedArr = entities ?? [];

  if (Array.isArray(selectedArr) && selectedArr.length > 0) {
    // bulk delete
    selectedArr.forEach((ent) => {
      if (!ent) return;

      // root collection
      if (viewer.entities?.contains?.(ent)) {
        // removeVertexMarkersFor(viewer, ent); // remove white points
        try {
          viewer.entities.remove(ent);
        } catch {}
        return;
      }

      // search data sources
      let removed = false;
      viewer.dataSources?._dataSources?.forEach?.((ds) => {
        if (removed) {
          return;
        }
        if (ds?.entities?.contains?.(ent)) {
          try {
            ds.entities.remove(ent);
          } catch {}
          removed = true;
          if (setMapObjects) {
            markEntityLayerEdited(ent, setMapObjects, viewer);
          }
        }
      });
    });
  }
};

export const mergeEntities = (selectedEntitiesRef) => {
  const selectedArr = selectedEntitiesRef.current ?? [];
  if (Array.isArray(selectedArr) && selectedArr.length > 0) {
    joinTwoSelectedPolylinesOnce({ selectedEntitiesRef });
  }
};

export const reverseWaypointsEntities = (
  viewer,
  selectedEntitiesRef,
  mapObjectsRef,
  setMapObjects,
  directionArrowSettingRef,
  entitiesOverride, // optional
) => {
  const selectedArr = entitiesOverride && Array.isArray(entitiesOverride) ? entitiesOverride : (selectedEntitiesRef.current ?? []);

  const userData = useAppStore.getState().userData;

  if (Array.isArray(selectedArr) && selectedArr.length > 0) {
    selectedArr.forEach((ent) => {
      if (!ent) return;

      const selectedEntity = ent;
      enableReverseOnClick(
        viewer,
        (e) => {
          // mark layer as edited
          const temp = [...(mapObjectsRef?.current ?? [])];
          for (const obj of temp) {
            if (obj.dataSource?.entities?.contains(e)) {
              obj.is_edited = true;
              break;
            }
          }
          setMapObjects?.(temp);
        },
        selectedEntity,
        false,
        directionArrowSettingRef,
        userData,
      );
    });

    showToast("The selected entity’s path has been reversed", Type.NOTIFICATION.INFO);
  }
};

export const duplicateEntities = (selectedEntitiesRef) => {
  const selectedArr = selectedEntitiesRef.current ?? [];
  const created = [];
  const userData = useAppStore.getState().userData;

  if (Array.isArray(selectedArr) && selectedArr.length > 0) {
    selectedArr.forEach((ent) => {
      if (!ent) return;

      const dup = duplicateEntity(ent, { userData });
      if (dup) {
        created.push(dup);
      }
    });

    if (created.length) {
      showToast("Duplicated", Type.NOTIFICATION.SUCCESS);
    }
  }

  return { created };
};

export function getRandomLightColor() {
  // Each channel between 150–255 to ensure lightness
  const r = Math.floor(Math.random() * 106) + 150;
  const g = Math.floor(Math.random() * 106) + 150;
  const b = Math.floor(Math.random() * 106) + 150;
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

export function toCapUnderscore(input) {
  // handle null/undefined and trim
  const s = String(input || "").trim();

  // split on -, _ or whitespace; filter empty parts
  const parts = s.split(/[-_\s]+/).filter(Boolean);

  // capitalize first letter of each part, rest lower
  const caps = parts.map((p) => {
    const lower = p.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  });

  // join with underscore
  return caps.join("_");
}

export const enableGroupDrag = (viewer, entities, originalCameraControls, userData) => {
  // basic guard
  if (!viewer || !entities || entities.length === 0) return null;

  const scene = viewer.scene;
  const ellipsoid = scene.globe.ellipsoid;

  const handler = new ScreenSpaceEventHandler(scene.canvas);
  const modifier = getUsernameAndUserIdFormat(userData);

  // state
  let isDragging = false;
  let dragStartCartesian = null;
  let cameraLocked = false;

  // per-entity drag data
  // value: { type: "position" | "polyline", base, current }
  const dragItems = new Map();

  const translationScratch = new Cartesian3();

  // pick ground like in startRoadMarkingMoveMode
  const pickGround = (screenPos) => {
    if (!screenPos) return null;

    if (scene.pickPositionSupported) {
      const p = scene.pickPosition(screenPos);
      if (defined(p)) return p;
    }

    const c = viewer.camera.pickEllipsoid(screenPos, ellipsoid);
    if (defined(c)) return c;

    const ray = viewer.camera.getPickRay(screenPos);
    if (!defined(ray)) return null;
    return scene.globe.pick(ray, scene);
  };

  // LEFT_DOWN: start group drag
  handler.setInputAction((click) => {
    const picked = scene.pick(click.position);
    if (!defined(picked) || !picked.id) return;

    const pickedEntity = picked.id;

    // only drag if clicked entity is in group
    const isMember = entities.some((ent) => ent.id === pickedEntity.id);
    if (!isMember) return;

    const cartesian = pickGround(click.position);
    if (!cartesian) return;

    dragStartCartesian = Cartesian3.clone(cartesian);
    dragItems.clear();

    const time = viewer.clock.currentTime;

    // prepare drag data for all entities in group
    entities.forEach((ent) => {
      let info = null;

      // case 1: simple position
      if (ent.position) {
        let pos;
        if (typeof ent.position.getValue === "function") {
          pos = ent.position.getValue(time);
        } else {
          pos = ent.position;
        }

        if (pos) {
          const base = Cartesian3.clone(pos);
          const current = Cartesian3.clone(pos);

          // live position via CallbackProperty
          ent.position = new CallbackProperty(() => current, false);

          info = {
            type: "position",
            base,
            current,
          };
        }
      }

      // case 2: polyline positions
      if (!info && ent.polyline && ent.polyline.positions) {
        const posProp = ent.polyline.positions;
        let positions;

        if (typeof posProp.getValue === "function") {
          positions = posProp.getValue(time);
        } else {
          positions = posProp;
        }

        if (positions && positions.length) {
          const base = positions.map((p) => Cartesian3.clone(p));
          const current = positions.map((p) => Cartesian3.clone(p));

          // live positions via CallbackProperty
          ent.polyline.positions = new CallbackProperty(() => current, false);

          info = {
            type: "polyline",
            base,
            current,
          };
        }
      }

      if (info) {
        dragItems.set(ent, info);
      }
    });

    // if nothing to drag, exit
    if (dragItems.size === 0) {
      dragStartCartesian = null;
      return;
    }

    // lock camera
    if (originalCameraControls && !cameraLocked) {
      disableCameraControls(viewer, originalCameraControls);
      cameraLocked = true;
    }

    isDragging = true;
    scene.requestRender();
  }, ScreenSpaceEventType.LEFT_DOWN);

  // MOUSE_MOVE: update all entities smoothly
  handler.setInputAction((movement) => {
    if (!isDragging || !dragStartCartesian || dragItems.size === 0) return;

    const screenPos = movement.endPosition || movement.startPosition || movement.position;
    if (!screenPos) return;

    const newCartesian = pickGround(screenPos);
    if (!newCartesian) return;

    // translation vector from drag start
    Cartesian3.subtract(newCartesian, dragStartCartesian, translationScratch);

    // apply translation to all drag items
    dragItems.forEach((info, ent) => {
      if (info.type === "position") {
        // base + translation -> current (mutate same object)
        Cartesian3.add(info.base, translationScratch, info.current);

        // optional: update lon/lat/height properties
        if (ent.properties) {
          const carto = Cartographic.fromCartesian(info.current);
          const lonDeg = CesiumMath.toDegrees(carto.longitude);
          const latDeg = CesiumMath.toDegrees(carto.latitude);
          const height = carto.height;

          if (ent.properties.lon) {
            if (typeof ent.properties.lon.setValue === "function") {
              ent.properties.lon.setValue(lonDeg);
            } else {
              ent.properties.lon = new ConstantProperty(lonDeg);
            }
          }

          if (ent.properties.lat) {
            if (typeof ent.properties.lat.setValue === "function") {
              ent.properties.lat.setValue(latDeg);
            } else {
              ent.properties.lat = new ConstantProperty(latDeg);
            }
          }

          if (ent.properties.height) {
            if (typeof ent.properties.height.setValue === "function") {
              ent.properties.height.setValue(height);
            } else {
              ent.properties.height = new ConstantProperty(height);
            }
          }
        }
      } else if (info.type === "polyline") {
        const basePositions = info.base;
        const currentPositions = info.current;

        // translate each vertex
        for (let i = 0; i < basePositions.length; i++) {
          Cartesian3.add(basePositions[i], translationScratch, currentPositions[i]);
        }
      }
    });

    // force re-render in requestRenderMode
    scene.requestRender();
  }, ScreenSpaceEventType.MOUSE_MOVE);

  // LEFT_UP: bake final positions and clean up
  handler.setInputAction(() => {
    if (!isDragging) return;

    // bake back into ConstantProperty for each entity
    dragItems.forEach((info, ent) => {
      if (info.type === "position") {
        const finalPos = Cartesian3.clone(info.current);

        ent.position = new ConstantPositionProperty(finalPos);
      } else if (info.type === "polyline") {
        const finalPositions = info.current.map((p) => Cartesian3.clone(p));

        ent.polyline.positions = new ConstantProperty(finalPositions);
      }

      if (modifier) {
        if (!ent.properties) {
          ent.properties = new PropertyBag();
        }

        if (typeof ent.properties.addProperty === "function" && !ent.properties.modifier) {
          ent.properties.addProperty("modifier");
        }

        ent.properties.modifier = new ConstantProperty(modifier);
      }
    });

    // reset state
    dragItems.clear();
    dragStartCartesian = null;
    isDragging = false;

    // restore camera
    if (cameraLocked && originalCameraControls) {
      restoreCameraControls(viewer, originalCameraControls);
      cameraLocked = false;
    }

    scene.requestRender();
  }, ScreenSpaceEventType.LEFT_UP);

  // patch destroy to restore camera if needed
  const originalDestroy = handler.destroy.bind(handler);
  handler.destroy = () => {
    if (cameraLocked && originalCameraControls) {
      restoreCameraControls(viewer, originalCameraControls);
      cameraLocked = false;
    }
    dragItems.clear();
    originalDestroy();
  };

  return handler;
};

// Make sure Cesium is imported or available in scope

export const flyToBBox = (viewer, entity, options = {}) => {
  // Options for control
  const {
    duration = 0.8, // flight duration
    tight = 1.5, // < 1 => more zoomed in, 1 => exact bbox
  } = options;

  if (!viewer || !entity) return;

  const time = viewer.clock.currentTime;

  // Helper to get positions or rectangle
  const extractData = () => {
    // Polygon
    if (entity.polygon?.hierarchy) {
      const h = entity.polygon.hierarchy.getValue(time);
      return h ? h.positions : [];
    }

    // Polyline
    if (entity.polyline?.positions) {
      return entity.polyline.positions.getValue(time) || [];
    }

    // Rectangle entity (already bbox)
    if (entity.rectangle?.coordinates) {
      const rect = entity.rectangle.coordinates.getValue(time);
      if (rect) return rect;
    }

    // Point / Billboard / Model with position
    if (entity.position) {
      const p = entity.position.getValue(time);
      return p ? [p] : [];
    }

    return [];
  };

  const data = extractData();

  // If direct rectangle
  if (data instanceof Rectangle) {
    let rect = data;

    // Tight zoom (shrink rectangle around its center)
    if (tight !== 1.0) {
      const west = rect.west;
      const east = rect.east;
      const south = rect.south;
      const north = rect.north;

      const centerLon = (west + east) / 2;
      const centerLat = (south + north) / 2;
      const halfLon = ((east - west) / 2) * tight;
      const halfLat = ((north - south) / 2) * tight;

      rect = new Rectangle(centerLon - halfLon, centerLat - halfLat, centerLon + halfLon, centerLat + halfLat);
    }

    viewer.camera.flyTo({
      destination: rect,
      duration,
    });

    return;
  }

  // If no positions
  if (!Array.isArray(data) || data.length === 0) return;

  // Build bbox from points (no huge padding!)
  let rect = Rectangle.fromCartesianArray(data);

  // Tight zoom (shrink bbox a bit)
  if (tight !== 1.0) {
    const west = rect.west;
    const east = rect.east;
    const south = rect.south;
    const north = rect.north;

    const centerLon = (west + east) / 2;
    const centerLat = (south + north) / 2;
    const halfLon = ((east - west) / 2) * tight;
    const halfLat = ((north - south) / 2) * tight;

    rect = new Rectangle(centerLon - halfLon, centerLat - halfLat, centerLon + halfLon, centerLat + halfLat);
  }

  // Fly to bbox
  viewer.camera.flyTo({
    destination: rect,
    duration,
  });
};

export function trimDecimals(value, maxDecimals = 7) {
  const rounded = Number(value);
  const str = rounded.toString();

  if (!str.includes(".")) return str;

  const [intPart, decPart] = str.split(".");
  return intPart + "." + decPart.substring(0, maxDecimals);
}

export const sortList = createListCollection({
  items: [
    { label: "Name", value: "name" },
    { label: "Type", value: "type" },
    { label: "Priority", value: "priority" },
    { label: "Changed", value: "changed" },
    { label: "Route", value: "route" },
  ],
});

export const SUFFIX_TO_TYPE = {
  ".geojson": Type.ASSET.GEOJSON,
  ".json": Type.ASSET.THREEDTILES,
  ".csv": Type.ASSET.CSV,
  ".glb": Type.ASSET.GLB,
  ".osm": Type.ASSET.OSM,
  ".zip": Type.ASSET.ZIP,
  ".kml": Type.ASSET.KML,
  ".kmz": Type.ASSET.KMZ,
};

export const getCombinedKeysText = (savedHotkeysRef, type) => {
  const keys = savedHotkeysRef.current.find((h) => h.type === type)?.combination || [];
  return `(${keys.join(" + ")})`;
};

// Sum total length of all polylines in a Cesium DataSource and return miles.
export function getDataSourcePolylineMiles(dataSource, options = {}) {
  if (!dataSource || !dataSource.entities) return 0;

  const {
    time = JulianDate.now(),
    ellipsoid = Ellipsoid.WGS84,
    includeHeight = false, // true => sqrt(surface^2 + deltaHeight^2)
  } = options;

  const METERS_PER_MILE = 1609.344;
  let totalMeters = 0;

  const entities = dataSource.entities.values || [];
  for (const e of entities) {
    const polyline = e.polyline;
    if (!polyline || !polyline.positions) continue;

    const positions = polyline.positions.getValue(time);
    if (!positions || positions.length < 2) continue;

    for (let i = 1; i < positions.length; i++) {
      const a = positions[i - 1];
      const b = positions[i];

      const ca = ellipsoid.cartesianToCartographic(a);
      const cb = ellipsoid.cartesianToCartographic(b);
      if (!ca || !cb) continue;

      const geodesic = new EllipsoidGeodesic(ca, cb, ellipsoid);
      let seg = geodesic.surfaceDistance; // meters on ellipsoid surface

      if (includeHeight) {
        const dh = (cb.height || 0) - (ca.height || 0);
        seg = Math.sqrt(seg * seg + dh * dh);
      }

      totalMeters += seg;
    }
  }

  return totalMeters / METERS_PER_MILE;
}

export function extractBaseName(input) {
  const last = String(input).split("/").filter(Boolean).pop() || "";
  return last
    .replace(/\.[a-z0-9]+$/i, "") // remove extension like .geojson
    .replace(/_\d+$/, ""); // remove trailing _timestamp
}

export const getUsernameAndUserIdFormat = (userData) => {
  const username = userData?.user?.username;
  const userId = userData?.user?.id;

  if (!username || !userId) return null;

  const prefix = username.slice(0, 2).toUpperCase();
  const formattedId = `${prefix}_${userId}`;

  return formattedId;
};

export const toDeg = (p) => {
  const c = Cartographic.fromCartesian(p);
  return {
    lon: CesiumMath.toDegrees(c.longitude),
    lat: CesiumMath.toDegrees(c.latitude),
    height: c.height,
  };
};
